// EmergencyPage.js

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import {
  AlertCircle,
  ArrowRight,
  Phone,
  MapPin,
  Navigation,
  Search,
  Clock,
  Building2,
  Heart,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

import SEO from '../components/SEO';

const EmergencyPage = () => {
  const [location, setLocation] = useState(null);
  const [postalCode, setPostalCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [activeTab, setActiveTab] = useState('urgences');

  // Numéros d'urgence
  const emergencyNumbers = [
    {
      number: '15',
      name: 'SAMU - Urgences vitales',
      description: 'Accident, malaise, hémorragie',
      color: 'red',
      icon: Phone,
    },
    {
      number: '17',
      name: 'Police Secours',
      description: 'Danger immédiat, violence',
      color: 'blue',
      icon: Phone,
    },
    {
      number: '18',
      name: 'Pompiers',
      description: 'Incendie, accident, secours',
      color: 'orange',
      icon: Phone,
    },
    {
      number: '112',
      name: "Numéro d'urgence européen",
      description: 'Toutes urgences',
      color: 'purple',
      icon: Phone,
    },
  ];

  // Demander la géolocalisation
  const getLocation = () => {
    setLoading(true);

    if (!navigator.geolocation) {
      toast.error('Géolocalisation non supportée par votre navigateur');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setLocation(coords);
        searchNearbyPlaces(coords);
        toast.success('Position détectée');
      },
      (error) => {
        console.error('Erreur géolocalisation:', error);
        toast.error("Impossible d'obtenir votre position");
        setLoading(false);
      }
    );
  };

  // Rechercher par code postal
  const searchByPostalCode = async () => {
    if (!postalCode || postalCode.length !== 5) {
      toast.error('Entrez un code postal valide (5 chiffres)');
      return;
    }

    setLoading(true);

    try {
      // Geocoding via API Gouv
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${postalCode}&type=municipality&limit=1`
      );
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const coords = {
          lat: data.features[0].geometry.coordinates[1],
          lng: data.features[0].geometry.coordinates[0],
        };
        setLocation(coords);
        searchNearbyPlaces(coords);
        toast.success('Localisation trouvée');
      } else {
        toast.error('Code postal introuvable');
        setLoading(false);
      }
    } catch (error) {
      console.error('Erreur recherche:', error);
      toast.error('Erreur lors de la recherche');
      setLoading(false);
    }
  };

  // Rechercher pharmacies et hôpitaux à proximité
  const searchNearbyPlaces = async (coords) => {
    try {
      // ⚠️ Note: Google Places API nécessite une clé API côté serveur
      // Alternative: utiliser l'API Overpass (OpenStreetMap) gratuite

      const radius = 5000; // 5km

      // Recherche pharmacies via Overpass API (OpenStreetMap)
      const pharmacyQuery = `
        [out:json];
        (
          node["amenity"="pharmacy"](around:${radius},${coords.lat},${coords.lng});
          way["amenity"="pharmacy"](around:${radius},${coords.lat},${coords.lng});
        );
        out body;
      `;

      const pharmacyResponse = await fetch(
        `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(pharmacyQuery)}`
      );
      const pharmacyData = await pharmacyResponse.json();

      // Recherche hôpitaux
      const hospitalQuery = `
        [out:json];
        (
          node["amenity"="hospital"](around:${radius},${coords.lat},${coords.lng});
          way["amenity"="hospital"](around:${radius},${coords.lat},${coords.lng});
        );
        out body;
      `;

      const hospitalResponse = await fetch(
        `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(hospitalQuery)}`
      );
      const hospitalData = await hospitalResponse.json();

      // Formater les résultats
      const formattedPharmacies = pharmacyData.elements.slice(0, 5).map((place) => ({
        name: place.tags?.name || 'Pharmacie',
        address: place.tags?.['addr:street'] || 'Adresse non disponible',
        phone: place.tags?.phone || null,
        distance: calculateDistance(coords, {
          lat: place.lat || place.center?.lat,
          lng: place.lon || place.center?.lon,
        }),
      }));

      const formattedHospitals = hospitalData.elements.slice(0, 5).map((place) => ({
        name: place.tags?.name || 'Hôpital',
        address: place.tags?.['addr:street'] || 'Adresse non disponible',
        phone: place.tags?.phone || null,
        emergency: place.tags?.emergency === 'yes',
        distance: calculateDistance(coords, {
          lat: place.lat || place.center?.lat,
          lng: place.lon || place.center?.lon,
        }),
      }));

      setPharmacies(formattedPharmacies);
      setHospitals(formattedHospitals);
    } catch (error) {
      console.error('Erreur recherche lieux:', error);
      toast.error('Erreur lors de la recherche des établissements');
    } finally {
      setLoading(false);
    }
  };

  // Calculer la distance entre deux points
  const calculateDistance = (coord1, coord2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = ((coord2.lat - coord1.lat) * Math.PI) / 180;
    const dLon = ((coord2.lng - coord1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((coord1.lat * Math.PI) / 180) *
        Math.cos((coord2.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance.toFixed(1);
  };

  return (
    <DashboardLayout>
      <SEO
        title="Urgences"
        description="Numéros d'urgence, pharmacies, hôpitaux et défibrillateurs à proximité."
        path="/emergency"
      />
      <div className="max-w-6xl mx-auto py-8 px-4" data-testid="emergency-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-2xl mb-6 shadow-lg">
            <AlertCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-4xl font-heading font-bold text-foreground dark:text-gray-100 mb-4">
            Urgences et Ressources
          </h1>
          <p className="text-lg text-muted dark:text-gray-400 max-w-2xl mx-auto">
            Numéros d'urgence et établissements à proximité
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['urgences', 'proximite', 'ecoute'].map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              variant={activeTab === tab ? 'default' : 'outline'}
              className={`${
                activeTab === tab
                  ? 'bg-red-600 dark:bg-red-700 text-white'
                  : 'dark:border-gray-700 dark:text-gray-300'
              }`}
            >
              {tab === 'urgences' && '🚨 Urgences'}
              {tab === 'proximite' && '📍 À proximité'}
              {tab === 'ecoute' && '💬 Écoute & Soutien'}
            </Button>
          ))}
        </div>

        {/* Onglet Urgences */}
        {activeTab === 'urgences' && (
          <div className="grid gap-4">
            {/* SAMU - 15 */}
            <Card className="p-6 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-red-600 dark:bg-red-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-bold text-red-900 dark:text-red-300">
                    SAMU - Urgences vitales
                  </h3>
                  <p className="text-red-700 dark:text-red-400 text-sm">
                    Accident, malaise, hémorragie
                  </p>
                </div>
                <a
                  href="tel:15"
                  className="text-4xl font-bold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                >
                  15
                </a>
              </div>
            </Card>

            {/* Police - 17 */}
            <Card className="p-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-600 dark:bg-blue-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-bold text-blue-900 dark:text-blue-300">
                    Police Secours
                  </h3>
                  <p className="text-blue-700 dark:text-blue-400 text-sm">
                    Danger immédiat, violence
                  </p>
                </div>
                <a
                  href="tel:17"
                  className="text-4xl font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  17
                </a>
              </div>
            </Card>

            {/* Pompiers - 18 */}
            <Card className="p-6 bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-orange-600 dark:bg-orange-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-bold text-orange-900 dark:text-orange-300">
                    Pompiers
                  </h3>
                  <p className="text-orange-700 dark:text-orange-400 text-sm">
                    Incendie, accident, secours
                  </p>
                </div>
                <a
                  href="tel:18"
                  className="text-4xl font-bold text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors"
                >
                  18
                </a>
              </div>
            </Card>

            {/* 112 */}
            <Card className="p-6 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-purple-600 dark:bg-purple-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-bold text-purple-900 dark:text-purple-300">
                    Numéro d'urgence européen
                  </h3>
                  <p className="text-purple-700 dark:text-purple-400 text-sm">Toutes urgences</p>
                </div>
                <a
                  href="tel:112"
                  className="text-4xl font-bold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
                >
                  112
                </a>
              </div>
            </Card>
          </div>
        )}

        {/* Onglet À proximité */}
        {activeTab === 'proximite' && (
          <div className="space-y-6">
            {/* Localisation */}
            <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-600" />
                Votre position
              </h3>

              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <Button
                  onClick={getLocation}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Navigation className="w-5 h-5 mr-2" />
                  )}
                  Utiliser ma position
                </Button>

                <div className="flex-1 flex gap-2">
                  <Input
                    type="text"
                    placeholder="Code postal (ex: 75001)"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                    className="dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                  />
                  <Button
                    onClick={searchByPostalCode}
                    disabled={loading}
                    variant="outline"
                    className="dark:border-gray-700 dark:text-gray-300"
                  >
                    <Search className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {location && (
                <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Position détectée : {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                </p>
              )}
            </Card>

            {/* Pharmacies */}
            {pharmacies.length > 0 && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-green-600" />
                  Pharmacies à proximité
                </h3>
                <div className="grid gap-4">
                  {pharmacies.map((pharmacy, idx) => (
                    <Card
                      key={idx}
                      className="bg-white dark:bg-gray-900 border-2 border-green-200 dark:border-green-800 p-4 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">
                            {pharmacy.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {pharmacy.address}
                          </p>
                          {pharmacy.phone && (
                            <a
                              href={`tel:${pharmacy.phone}`}
                              className="text-sm text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              {pharmacy.phone}
                            </a>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="inline-block px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium">
                            {pharmacy.distance} km
                          </span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Hôpitaux */}
            {hospitals.length > 0 && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-blue-600" />
                  Hôpitaux à proximité
                </h3>
                <div className="grid gap-4">
                  {hospitals.map((hospital, idx) => (
                    <Card
                      key={idx}
                      className="bg-white dark:bg-gray-900 border-2 border-blue-200 dark:border-blue-800 p-4 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-gray-900 dark:text-gray-100">
                              {hospital.name}
                            </h4>
                            {hospital.emergency && (
                              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                                Urgences
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {hospital.address}
                          </p>
                          {hospital.phone ? (
                            <a
                              href={`tel:${hospital.phone}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              {hospital.phone}
                            </a>
                          ) : hospital.emergency ? (
                            <a
                              href="tel:15"
                              className="text-sm text-red-600 dark:text-red-400 hover:underline flex items-center gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              Appeler le 15 (SAMU)
                            </a>
                          ) : (
                            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              Numéro non disponible
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                            {hospital.distance} km
                          </span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {!location && !loading && (
              <Card className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 border-gray-200 dark:border-gray-700 p-12 text-center">
                <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Localisez-vous pour voir les établissements à proximité
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Utilisez votre position ou entrez un code postal
                </p>
              </Card>
            )}
          </div>
        )}

        {/* Onglet Écoute & Soutien */}
        {activeTab === 'ecoute' && (
          <div className="grid gap-4">
            {/* SOS Médecins */}
            <Card className="p-6 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-600 dark:bg-green-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-900 dark:text-green-300">
                    SOS Médecins
                  </h3>
                  <p className="text-green-700 dark:text-green-400 text-sm">
                    Consultation médicale à domicile
                  </p>
                </div>
                <a
                  href="tel:3624"
                  className="text-2xl font-bold text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                >
                  3624
                </a>
              </div>
            </Card>

            {/* Médecin de garde */}
            <Card className="p-6 bg-teal-50 dark:bg-teal-950/20 border-teal-200 dark:border-teal-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-teal-600 dark:bg-teal-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-teal-900 dark:text-teal-300">
                    Médecin de garde
                  </h3>
                  <p className="text-teal-700 dark:text-teal-400 text-sm">
                    En dehors des heures d'ouverture
                  </p>
                </div>
                <a
                  href="tel:116117"
                  className="text-2xl font-bold text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                >
                  116 117
                </a>
              </div>
            </Card>

            {/* Centre Antipoison */}
            <Card className="p-6 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-600 dark:bg-amber-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-amber-900 dark:text-amber-300">
                    Centre Antipoison Paris
                  </h3>
                  <p className="text-amber-700 dark:text-amber-400 text-sm">
                    Intoxication, empoisonnement
                  </p>
                </div>
                <a
                  href="tel:0140054848"
                  className="text-xl font-bold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                >
                  01 40 05 48 48
                </a>
              </div>
            </Card>

            {/* Prévention suicide */}
            <Card className="p-6 bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-indigo-600 dark:bg-indigo-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-indigo-900 dark:text-indigo-300">
                    Prévention suicide
                  </h3>
                  <p className="text-indigo-700 dark:text-indigo-400 text-sm">
                    Écoute et soutien 24h/24
                  </p>
                </div>
                <a
                  href="tel:3114"
                  className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                >
                  3114
                </a>
              </div>
            </Card>

            {/* Croix-Rouge Écoute */}
            <Card className="p-6 bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-rose-600 dark:bg-rose-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-rose-900 dark:text-rose-300">
                    Croix-Rouge Écoute
                  </h3>
                  <p className="text-rose-700 dark:text-rose-400 text-sm">Solitude, détresse</p>
                </div>
                <a
                  href="tel:0972394050"
                  className="text-xl font-bold text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 transition-colors"
                >
                  09 72 39 40 50
                </a>
              </div>
            </Card>

            {/* Allo Maltraitance */}
            <Card className="p-6 bg-violet-50 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-violet-600 dark:bg-violet-700 rounded-full flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-violet-900 dark:text-violet-300">
                    Allo Maltraitance
                  </h3>
                  <p className="text-violet-700 dark:text-violet-400 text-sm">
                    Personnes âgées et handicapées
                  </p>
                </div>
                <a
                  href="tel:0800473333"
                  className="text-xl font-bold text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 transition-colors"
                >
                  0800 47 33 33
                </a>
              </div>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmergencyPage;
